<template>
  <div class="permission-management-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>权限管理</span>
      </div>
    </div>
    
    <!-- 搜索区域（仅支持权限名称、权限类型） -->
    <div class="search-area">
      <el-input
        v-model="searchParams.permissionName"
        placeholder="权限名称"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      <el-select
        v-model="searchParams.permissionType"
        placeholder="权限类型"
        clearable
        class="search-input"
      >
        <el-option label="菜单权限" value="MENU"></el-option>
        <el-option label="按钮权限" value="BUTTON"></el-option>
        <el-option label="API权限" value="API"></el-option>
      </el-select>
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      <el-button
        v-permission="'system:permission-management:create'"
        type="primary"
        @click="handleCreate"
        icon="el-icon-plus"
        style="margin-left: 10px;"
      >新增权限</el-button>
    </div>
    
    <!-- 表格区域 -->
    <el-table
      :data="permissionList"
      border
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
    >
      <el-table-column prop="permissionCode" label="权限码" min-width="200" align="center"></el-table-column>
      <el-table-column prop="permissionName" label="权限名称" min-width="150" align="center"></el-table-column>
      <el-table-column prop="permissionType" label="权限类型" width="120" align="center">
        <template slot-scope="scope">
          <el-tag :type="getTypeTagType(scope.row.permissionType)">
            {{ getTypeText(scope.row.permissionType) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="resourcePath" label="资源路径" min-width="200" align="center"></el-table-column>
      <el-table-column prop="sortOrder" label="排序" width="80" align="center"></el-table-column>
      <el-table-column label="显示在菜单" width="100" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.permissionType === 'MENU'">
            {{ scope.row.showInMenu ? '是' : '否' }}
          </span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" min-width="200" align="center"></el-table-column>
      <el-table-column label="创建时间" width="180" align="center">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button
            v-permission="'system:permission-management:edit'"
            type="primary"
            size="mini"
            @click="handleEdit(scope.row)"
          >编辑</el-button>
          <el-button
            v-permission="'system:permission-management:delete'"
            type="danger"
            size="mini"
            @click="handleDelete(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
      ></el-pagination>
    </div>
    
    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogStatus === 'add' ? '新增权限' : '编辑权限'"
      :visible.sync="dialogVisible"
      width="700px"
      :close-on-click-modal="false"
    >
      <el-form
        :model="permissionForm"
        :rules="rules"
        ref="permissionForm"
        label-width="120px"
      >
        <el-form-item label="权限码" prop="permissionCode">
          <el-input v-model="permissionForm.permissionCode" placeholder="请输入权限码，如：system:role-management:create"></el-input>
        </el-form-item>
        <el-form-item label="权限名称" prop="permissionName">
          <el-input v-model="permissionForm.permissionName" placeholder="请输入权限名称"></el-input>
        </el-form-item>
        <el-form-item label="权限类型" prop="permissionType">
          <el-select v-model="permissionForm.permissionType" placeholder="请选择权限类型" style="width: 100%">
            <el-option label="菜单权限" value="MENU"></el-option>
            <el-option label="按钮权限" value="BUTTON"></el-option>
            <el-option label="API权限" value="API"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="资源路径" prop="resourcePath">
          <el-input v-model="permissionForm.resourcePath" placeholder="请输入资源路径（菜单路径/API路径/按钮标识）"></el-input>
        </el-form-item>
        <el-form-item label="父权限" prop="parentId">
          <el-select
            v-model="permissionForm.parentId"
            placeholder="请选择父权限（可选）"
            filterable
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="perm in parentPermissionOptions"
              :key="perm.id"
              :label="`${perm.permissionCode} - ${perm.permissionName}`"
              :value="perm.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="permissionForm.sortOrder" :min="0" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item v-if="permissionForm.permissionType === 'MENU'" label="显示在菜单" prop="showInMenu">
          <el-switch v-model="permissionForm.showInMenu" active-text="是" inactive-text="否"></el-switch>
          <span class="form-tip">开启后，该菜单将显示在左侧导航栏</span>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="permissionForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入权限描述"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  query_permissions_pagination,
  create_permission,
  update_permission,
  delete_permission,
  get_permission_tree
} from '@/api/modules/permission'
import moment from 'moment'

export default {
  name: 'PermissionManagement',
  data() {
    return {
      loading: false,
      permissionList: [],
      searchParams: {
        permissionName: '',
        permissionType: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogStatus: 'add', // 'add' 或 'edit'
      permissionForm: {
        id: null,
        permissionCode: '',
        permissionName: '',
        permissionType: '',
        resourcePath: '',
        parentId: null,
        sortOrder: 0,
        showInMenu: true,
        description: ''
      },
      rules: {
        permissionCode: [
          { required: true, message: '请输入权限码', trigger: 'blur' }
        ],
        permissionName: [
          { required: true, message: '请输入权限名称', trigger: 'blur' }
        ],
        permissionType: [
          { required: true, message: '请选择权限类型', trigger: 'change' }
        ]
      },
      parentPermissionOptions: []
    }
  },
  created() {
    this.fetchPermissionList()
    this.fetchParentPermissions()
  },
  methods: {
    // 获取权限列表
    async fetchPermissionList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage - 1,
          size: this.pagination.pageSize
        }
        
        if (this.searchParams.permissionName) {
          params.permissionName = this.searchParams.permissionName
        }
        if (this.searchParams.permissionType) {
          params.permissionType = this.searchParams.permissionType
        }
        
        const response = await query_permissions_pagination(params)
        
        if (response.data && response.data.success) {
          const data = response.data.data
          this.permissionList = data.content || []
          this.pagination.total = data.totalElements || 0
          this.pagination.currentPage = (data.number || 0) + 1
        } else {
          this.$message.error(response.data.message || '查询失败')
        }
      } catch (error) {
        console.error('查询权限列表失败:', error)
        this.$message.error('查询失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    
    // 获取父权限选项（用于下拉选择）
    async fetchParentPermissions() {
      try {
        const response = await get_permission_tree()
        if (response.data && response.data.success) {
          const tree = response.data.data || []
          // 扁平化权限树，用于下拉选择
          this.parentPermissionOptions = this.flattenPermissionTree(tree)
        }
      } catch (error) {
        console.error('获取父权限列表失败:', error)
      }
    },
    
    // 扁平化权限树
    flattenPermissionTree(tree, result = []) {
      tree.forEach(node => {
        result.push({
          id: node.id,
          permissionCode: node.permissionCode,
          permissionName: node.permissionName,
          permissionType: node.permissionType
        })
        if (node.children && node.children.length > 0) {
          this.flattenPermissionTree(node.children, result)
        }
      })
      return result
    },
    
    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1
      this.fetchPermissionList()
    },
    
    // 新增权限
    handleCreate() {
      this.dialogStatus = 'add'
      this.permissionForm = {
        id: null,
        permissionCode: '',
        permissionName: '',
        permissionType: 'MENU',
        resourcePath: '',
        parentId: null,
        sortOrder: 0,
        showInMenu: true,
        description: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.permissionForm) {
          this.$refs.permissionForm.clearValidate()
        }
      })
    },
    
    // 编辑权限
    handleEdit(row) {
      this.dialogStatus = 'edit'
      this.permissionForm = {
        id: row.id,
        permissionCode: row.permissionCode,
        permissionName: row.permissionName,
        permissionType: row.permissionType,
        resourcePath: row.resourcePath || '',
        parentId: row.parentId || null,
        sortOrder: row.sortOrder || 0,
        showInMenu: row.permissionType === 'MENU' ? (row.showInMenu !== false) : false,
        description: row.description || ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.permissionForm) {
          this.$refs.permissionForm.clearValidate()
        }
      })
    },
    
    // 删除权限
    handleDelete(row) {
      this.$confirm('确认删除该权限?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await delete_permission(row.id)
          if (response.data && response.data.success) {
            this.$message.success('删除成功')
            this.fetchPermissionList()
            this.fetchParentPermissions() // 刷新父权限选项
          } else {
            this.$message.error(response.data.message || '删除失败')
          }
        } catch (error) {
          console.error('删除权限失败:', error)
          this.$message.error('删除失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        }
      }).catch(() => {})
    },
    
    // 提交表单
    async submitForm() {
      try {
        await this.$refs.permissionForm.validate()
        
        const formData = { ...this.permissionForm }
        // 如果parentId为空，设置为null
        if (!formData.parentId) {
          formData.parentId = null
        }
        // 仅菜单类型需要提交 showInMenu，非菜单类型不提交
        if (formData.permissionType !== 'MENU') {
          delete formData.showInMenu
        } else {
          formData.showInMenu = !!formData.showInMenu
        }
        
        if (this.dialogStatus === 'add') {
          const response = await create_permission(formData)
          if (response.data && response.data.success) {
            this.$message.success('创建成功')
            this.dialogVisible = false
            this.fetchPermissionList()
            this.fetchParentPermissions() // 刷新父权限选项
          } else {
            this.$message.error(response.data.message || '创建失败')
          }
        } else {
          const response = await update_permission(formData.id, formData)
          if (response.data && response.data.success) {
            this.$message.success('更新成功')
            this.dialogVisible = false
            this.fetchPermissionList()
            this.fetchParentPermissions() // 刷新父权限选项
          } else {
            this.$message.error(response.data.message || '更新失败')
          }
        }
      } catch (error) {
        if (error.message) {
          this.$message.error(error.message)
        }
      }
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
      this.fetchPermissionList()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.fetchPermissionList()
    },
    
    // 格式化日期时间
    formatDateTime(date) {
      return date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    
    // 获取权限类型标签类型
    getTypeTagType(type) {
      const typeMap = {
        MENU: 'success',
        BUTTON: 'warning',
        API: 'info'
      }
      return typeMap[type] || ''
    },
    
    // 获取权限类型文本
    getTypeText(type) {
      const typeMap = {
        MENU: '菜单权限',
        BUTTON: '按钮权限',
        API: 'API权限'
      }
      return typeMap[type] || type
    }
  }
}
</script>

<style scoped>
.permission-management-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.header-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
}

.title-mark {
  width: 4px;
  height: 18px;
  background: #409EFF;
  margin-right: 10px;
}

.search-area {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.search-input {
  width: 200px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.form-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
</style>
